import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { IEquipmentMaintenanceDraft } from '@d-orca/shared/equipment-maintenance';
import {
  createEquipmentMaintenance,
  deleteEquipmentMaintenance,
  fetchEquipmentMaintenance,
  fetchEquipmentMaintenanceLog,
  markEquipmentMaintenanceDone,
  updateEquipmentMaintenance,
} from '@apis/equipmentMaintenance';

const maintenanceKey = (siteId: string, equipmentId: string) => ['equipment', 'maintenance', siteId, equipmentId] as const;
const assetsKey = (siteId: string) => ['equipment', 'assets', siteId] as const;

/** An asset's maintenance deadlines, open ones first. */
export function useEquipmentMaintenance(siteId: string, equipmentId: string) {
  return useQuery({
    queryKey: maintenanceKey(siteId, equipmentId),
    queryFn: ({ signal }) => fetchEquipmentMaintenance(siteId, equipmentId, signal),
    staleTime: 30_000,
  });
}

/** Every completion on the asset, newest first. Under the schedule's key, so every write refreshes it. */
export function useEquipmentMaintenanceLog(siteId: string, equipmentId: string) {
  return useQuery({
    queryKey: [...maintenanceKey(siteId, equipmentId), 'records'],
    queryFn: ({ signal }) => fetchEquipmentMaintenanceLog(siteId, equipmentId, signal),
    staleTime: 30_000,
  });
}

/**
 * The schedule's writes. Each refreshes the schedule, its log and the registry
 * (an asset's "needs maintenance" flag follows its deadlines) whether it worked
 * or not: a failed write may mean someone changed it first. Failures are
 * toasted by the app's mutation backstop.
 */
export function useEquipmentMaintenanceWrites(siteId: string, equipmentId: string) {
  const queryClient = useQueryClient();
  const onSettled = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: maintenanceKey(siteId, equipmentId) }),
      queryClient.invalidateQueries({ queryKey: assetsKey(siteId) }),
    ]);

  const add = useMutation({
    mutationFn: (draft: IEquipmentMaintenanceDraft) => createEquipmentMaintenance(siteId, equipmentId, draft),
    onSettled,
  });

  const update = useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) =>
      updateEquipmentMaintenance(siteId, equipmentId, id, title),
    onSettled,
  });

  const markDone = useMutation({
    mutationFn: (id: string) => markEquipmentMaintenanceDone(siteId, equipmentId, id),
    onSettled,
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteEquipmentMaintenance(siteId, equipmentId, id),
    onSettled,
  });

  return { add, update, markDone, remove };
}
